import { Injectable } from '@angular/core';
import { LoadingService } from '@core/services/loading/loading.service';
import { TranslateService } from '@ngx-translate/core';
import { InfoModalComponent } from '@shared/components/info-modal/info-modal.component';
import { OtpFormComponent } from '@shared/components/otp-form/otp-form.component';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Observable, Subject, Subscription, timer } from 'rxjs';
import { startWith, switchMap, take, takeUntil } from 'rxjs/operators';
import {
    CONFIRM_REMOVE_TRUSTED_DEVICE,
    EMAIL_CODE,
    getModalConfigForErrorCode,
} from '../constants/modals-messages.constants';
import {
    InfoModalConfig,
    InfoModalResult,
    OtpModalChannel,
    OtpModalConfig,
    OtpModalResult,
} from '../models';

@Injectable({
  providedIn: 'root',
})
export class MosaicModalService {
  private modalRefOTP: BsModalRef | null = null;
  private modalRefInfo: BsModalRef | null = null;
  private otpEvents = new Subject<OtpModalResult>();
  private subsModal = new Subscription();
  private currentOTPChannel: OtpModalChannel | null = null;

  //Observable que maneja el temporizador para el cierre por inactividad del modal OTP
  public timer$!: Observable<number>;

  constructor(
    private modalService: BsModalService,
    private loadingService: LoadingService,
    private translateService: TranslateService
  ) {}

  public showOTPModal(channel: OtpModalChannel): Promise<OtpModalResult> {
    return this.openOTPModal(channel);
  }

  public hideOTPModal(): void {
    return this.closeOTPModal();
  }

  public getComponentOTP(): OtpFormComponent | null {
    return this.modalRefOTP?.content as OtpFormComponent || null;
  }

  public showErrorModal(initState: InfoModalConfig): Promise<InfoModalResult> {
    return this.openErrorModal(initState);
  }
  public showConfirmationModal(initialState?: InfoModalConfig): Promise<InfoModalResult> {
    // Si no se pasa estado, reutilizamos la constante de confirmación
    const state = initialState || CONFIRM_REMOVE_TRUSTED_DEVICE;
    return this.modalConfirmation(state);
  }

  // Mostrar modal de error usando un código de error (aprovecha el MAP de constantes)
  public showErrorByCode(errorCode?: string): Promise<InfoModalResult> {
    const modalConfig = getModalConfigForErrorCode(errorCode);
    return this.openErrorModal(modalConfig);
  }


  private openOTPModal(channel: OtpModalChannel): Promise<OtpModalResult> {
    if (!this.modalRefOTP || this.currentOTPChannel !== channel) {
      this.closeOTPModal();
      // console.log('[ModalRefOTP] Creando modalRef para OTP');
      this.createModalOTP(this.resolveOTPModalConfig(channel));
    }
    // console.log('[ModalRefOTP] Ya existe una referencia al modal');
    return this.otpEvents.pipe(take(1)).toPromise();
  }

  private createModalOTP(initialState?: OtpModalConfig): void {

    const modalDialogContent = {
      title: initialState?.title ? this.translateService.instant(initialState.title) : undefined,
      text: initialState?.text ? this.translateService.instant(initialState.text) : undefined,
      email: initialState?.email ?? false,
    };
    this.modalRefOTP = initialState
      ? this.modalService.show(OtpFormComponent,  { initialState: modalDialogContent })
      : this.modalService.show(OtpFormComponent);

    const otpComponent = this.modalRefOTP.content as OtpFormComponent;
    this.currentOTPChannel = this.resolveOTPChannel(initialState);

    // Cerrar el modal tras 5 minutos sin cambios en el OTP.
    const otpControl = otpComponent.form.get('secretKey');
    const inactivityMs = 5 * 60 * 1000; // 5 minutos

    if (otpControl) {
      const closeByInactivity$ = otpControl.valueChanges.pipe(
        startWith(otpControl.value),
        switchMap(() => timer(inactivityMs)),
        takeUntil(otpComponent.hide),
      );

      this.subsModal.add(
        closeByInactivity$.subscribe(() => {
          this.otpEvents.next({ action: 'cancel' });
          this.closeOTPModal();
        }),
      );
    }

    this.subsModal.add(
      otpComponent.send.subscribe((otp: string) => {
        // console.log('[OtpModalService] Submit OTP');
        this.otpEvents.next({ action: 'submit', otp });
        this.loadingService.show();
      })
    );

    this.subsModal.add(
      otpComponent.repeat.subscribe(() => {
        // console.log('[OtpModalService] Resend OTP requested');
        otpComponent.invalidCode = false;
        otpComponent.form.reset();
        this.otpEvents.next({ action: 'resend' });
        this.loadingService.show();
      })
    );

    this.subsModal.add(
      otpComponent.hide.pipe(take(1)).subscribe(() => {
        // console.log('[OtpModalService] Close OTP modal');
        this.otpEvents.next({ action: 'cancel' });
        this.closeOTPModal();
      })
    );
  }

  private closeOTPModal(): void {
    if (this.modalRefOTP) {
      this.modalRefOTP.hide();
    }
    this.modalRefOTP = null;
    this.currentOTPChannel = null;
    this.subsModal.unsubscribe();
    this.subsModal = new Subscription();

  }

  private openErrorModal(initialState: InfoModalConfig): Promise<InfoModalResult> {
    // Pasamos las claves al componente y dejamos que el pipe `translate` las resuelva.
    const modalDialogContent: InfoModalConfig = {
      head: initialState?.head,
      description: initialState?.description,
      type: initialState?.type || 'info',
    };
    return this.openInfoModal(modalDialogContent, false);
  }

  private modalConfirmation(initialState: InfoModalConfig): Promise<InfoModalResult> {

    const modalDialogContent: InfoModalConfig = {
      head: initialState?.head ? initialState.head : undefined,
      description: initialState?.description ? initialState.description : undefined,
      type: initialState?.type || 'info',
      hasCancelBtn: true,
    };
    return this.openInfoModal(modalDialogContent, true);
  }

  private openInfoModal(initialState: InfoModalConfig, isConfirmation: boolean): Promise<InfoModalResult> {

    return new Promise((resolve) => {
      // Asegurarse de que el loading esté oculto antes de mostrar el modal
      this.loadingService.hide();
      this.closeInfoModal();
      this.modalRefInfo = this.modalService.show(InfoModalComponent, { initialState });
      const infoComponent = this.modalRefInfo.content as InfoModalComponent;
      let resolved = false;

      const resolveModal = (result: InfoModalResult): void => {
        if (resolved) {
          return;
        }

        resolved = true;
        resolve(result);
        this.closeInfoModal();
      };

      if (isConfirmation) {
        infoComponent.callback!.pipe(take(1)).subscribe(() => {
          resolveModal({ action: 'ok' });
        });
      }

      infoComponent.hide.pipe(take(1)).subscribe(() => {
        resolveModal({ action: isConfirmation ? 'cancel' : 'ok' });
      });
    });
  }

  private closeInfoModal(): void {
    if (this.modalRefInfo) {
      this.modalRefInfo.hide();
    }

    this.modalRefInfo = null;
  }

  private resolveOTPModalConfig(channel: OtpModalChannel): OtpModalConfig | undefined {
    if (channel === OtpModalChannel.email) {
      return EMAIL_CODE;
    }

    return undefined;
  }

  private resolveOTPChannel(initialState?: OtpModalConfig): OtpModalChannel {
    return initialState ? OtpModalChannel.email : OtpModalChannel.sms;
  }
}
